import { motion } from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  isBlocked?: boolean;
}

export function ChatInput({ onSend, disabled, isBlocked }: ChatInputProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to resize when value changes
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Use requestAnimationFrame to ensure the DOM is ready for measurement
      // and avoid flickering on some mobile browsers
      requestAnimationFrame(() => {
        textarea.style.height = "auto";
        const newHeight = Math.min(textarea.scrollHeight, 150);
        textarea.style.height = `${newHeight}px`;
      });
    }
  }, [value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled) {
      onSend(value.trim());
      setValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      onSubmit={handleSubmit}
      className="backdrop-blur-md bg-white/60 border-stone-200/50 border-t flex gap-3 items-end px-4 py-4 relative"
    >
      <textarea
        ref={textareaRef}
        id="chat-message"
        name="message"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Type your message..."
        rows={1}
        className="bg-white border border-stone-200/80 disabled:cursor-not-allowed disabled:opacity-50 flex-1 focus:border-emerald-300 focus:shadow-emerald-900/10 focus:shadow-md leading-6 outline-none overflow-hidden placeholder:text-stone-400 px-4 py-3 resize-none rounded-2xl shadow-sm shadow-stone-900/5 text-[15px] text-stone-800 transition-[border-color,box-shadow,opacity] duration-200"
      />

      <motion.button
        type="submit"
        disabled={disabled || !value.trim()}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          shrink-0
          w-12 h-12
          flex items-center justify-center
          rounded-xl
          font-medium text-lg
          transition-all duration-200
          ${
            value.trim() && !disabled
              ? "bg-linear-to-br from-emerald-500 hover:shadow-emerald-900/30 hover:shadow-xl shadow-emerald-900/25 shadow-lg text-white to-teal-600"
              : "bg-stone-100 cursor-not-allowed text-stone-400"
          }
        `}
      >
        {isBlocked ? (
          // Lock icon when conversation is blocked
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-label="Conversation blocked"
            role="img"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        ) : disabled ? (
          // Loading spinner when waiting for response
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
            className="border-2 border-stone-300 border-t-stone-500 h-5 rounded-full w-5"
          />
        ) : (
          // Send icon when ready
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-label="Send message"
            role="img"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        )}
      </motion.button>
    </motion.form>
  );
}
